import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequests } from '../../shared/api/apiRequests.js';
import dayjs from 'dayjs';
import { Costs } from '../../entities/Costs/index.js';
import { toast } from 'react-toastify';
import { Form, Row, Col } from 'react-bootstrap';
import { FormLayout, FormSection, FormField } from '../../shared/ui/FormLayout';

const CreatePromocodePage = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [promocodeValue, setPromocodeValue] = useState([]);
  const [form, setForm] = useState({
    code: '',
    discriminator: 'PricePromocode',
    startDate: dayjs(new Date()).format('YYYY-MM-DDTHH:mm'),
    endDate: dayjs(new Date()).add(1, 'month').format('YYYY-MM-DDTHH:mm'),
    isReusable: true,
    priceFrom: null,
    priceTo: null,
  });

  useEffect(() => {
    form.discriminator === 'PricePromocode' ? setPromocodeValue([]) : setPromocodeValue(0);
  }, [form.discriminator]);

  const handleChangePrice = (value, changedItem) => {
    setForm((prev) => {
      const newPriceFrom = prev[changedItem]?.costs.map((i) => {
        if (i.countryId === value.countryId) {
          return {
            countryId: i.countryId,
            costs: i.costs.map((z) => {
              if (z.currencyId === value.value.currencyId) {
                return { currencyId: z.currencyId, value: value.value.newValue };
              } else {
                return z;
              }
            }),
          };
        } else {
          return i;
        }
      });
      return { ...prev, [changedItem]: { costs: newPriceFrom } };
    });
  };

  const handleChangePriceOfPromocode = (value) => {
    setPromocodeValue((prev) => {
      const newPriceFrom = promocodeValue.map((i) => {
        if (i.countryId === value.countryId) {
          return {
            countryId: i.countryId,
            costs: i.costs.map((z) => {
              if (z.currencyId === value.value.currencyId) {
                return { currencyId: z.currencyId, value: value.value.newValue };
              } else {
                return z;
              }
            }),
          };
        } else {
          return i;
        }
      });

      return newPriceFrom;
    });
  };

  const handleRequest = async () => {
    try {
      // Проверка на заполнение обязательных полей
      if (!form.code) {
        toast.error('Введите код промокода');
        return;
      }
      
      // Подготовка данных для отправки
      let discountValue;
      
      if (form.discriminator === 'PricePromocode') {
        // Для фиксированного промокода
        if (!promocodeValue || promocodeValue.length === 0) {
          toast.error('Укажите сумму скидки');
          return;
        }
        discountValue = { costs: promocodeValue };
      } else {
        // Для процентного промокода
        if (!promocodeValue && promocodeValue !== 0) {
          toast.error('Укажите процент скидки');
          return;
        }
        // Преобразуем строку в число
        discountValue = Number(promocodeValue);
        if (isNaN(discountValue)) {
          toast.error('Процент скидки должен быть числом');
          return;
        }
        if (discountValue < 0 || discountValue > 100) {
          toast.error('Процент скидки должен быть от 0 до 100');
          return;
        }
      }
      
      const body = {
        ...form,
        [form.discriminator === 'PricePromocode' ? 'discount' : 'percent']: discountValue,
      };
      
      console.log('Отправляемые данные:', body);
      
      const query = apiRequests.promocodes.create(body);
      await toast.promise(query, {
        pending: 'Запрос к серверу',
        error: 'Произошла ошибка',
        success: 'Промокод успешно создан',
      });
      
      // Перенаправление на страницу списка промокодов после успешного создания
      navigate('/shop/promocodes');
    } catch (error) {
      console.error('Ошибка при создании промокода:', error);
      toast.error('Произошла ошибка при создании промокода');
    }
  };

  return (
    <FormLayout
      onSubmit={handleRequest}
      onBack={() => navigate('/shop/promocodes')}
      saving={saving}
      submitText="Создать промокод"
      savingText="Создание..."
    >
      <FormSection title="Основная информация">
        <FormField label="Промокод" required>
          <Form.Control
            type="text"
            value={form.code}
            onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
            placeholder="Введите промокод"
          />
        </FormField>
      </FormSection>

      <FormSection title="Период действия">
        <Row>
          <Col md={6}>
            <FormField label="Действует с">
              <Form.Control
                type="datetime-local"
                value={form.startDate}
                onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))}
              />
            </FormField>
          </Col>
          <Col md={6}>
            <FormField label="Действует до">
              <Form.Control
                type="datetime-local"
                value={form.endDate}
                onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))}
              />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Настройки промокода">
        <Row>
          <Col md={6}>
            <FormField label="Повторное использование">
              <Form.Select
                value={form.isReusable}
                onChange={(e) => setForm((prev) => ({ ...prev, isReusable: e.target.value === 'true' }))}
              >
                <option value="true">Да</option>
                <option value="false">Нет</option>
              </Form.Select>
            </FormField>
          </Col>
          <Col md={6}>
            <FormField label="Тип промокода">
              <Form.Select
                value={form.discriminator}
                onChange={(e) => setForm((prev) => ({ ...prev, discriminator: e.target.value }))}
              >
                <option value="PricePromocode">Фиксированный</option>
                <option value="PercentPromocode">Проценты</option>
              </Form.Select>
            </FormField>
          </Col>
        </Row>

        <FormField label={form.discriminator === 'PricePromocode' ? 'Сумма скидки' : 'Процент скидки'}>
          {form.discriminator === 'PricePromocode' ? (
            <Costs
              usdOnly
              onChange={(val) => handleChangePriceOfPromocode(val)}
              onAdd={(value) => setPromocodeValue((prev) => [...prev, value])}
            />
          ) : (
            <Form.Control
              type="number"
              min="0"
              max="100"
              value={promocodeValue}
              onChange={(e) => setPromocodeValue(e.target.value)}
              placeholder="Введите значение"
            />
          )}
        </FormField>
      </FormSection>

      <FormSection title="Условия применения">
        <FormField label="Действует от суммы">
          <Costs
            usdOnly
            onChange={(val) => handleChangePrice(val, 'priceFrom')}
            onAdd={(value) =>
              setForm((prev) => ({
                ...prev,
                priceFrom: !prev.priceFrom
                  ? { costs: [value] }
                  : { costs: [...prev.priceFrom.costs, value] },
              }))
            }
          />
        </FormField>

        <FormField label="Действует до суммы">
          <Costs
            usdOnly
            onChange={(val) => handleChangePrice(val, 'priceTo')}
            onAdd={(value) =>
              setForm((prev) => ({
                ...prev,
                priceTo: !prev.priceTo
                  ? { costs: [value] }
                  : { costs: [...prev.priceTo.costs, value] },
              }))
            }
          />
        </FormField>
      </FormSection>

    </FormLayout>
  );
};

export default CreatePromocodePage;
